<div class="p-2 h-full overflow-y-auto">
  <div class="flex items-center justify-between mb-3">
    <div class="text-base font-semibold text-gray-900">Slack status</div>
    <button type="button" class="text-xs text-gray-500 hover:text-gray-900" (click)="refresh()" [disabled]="loading()">Refresh</button>
  </div>

  @if (loading()) {
    <div class="text-sm text-gray-500">Loading…</div>
  } @else if (!connected()) {
    <div class="rounded-lg border border-border-subtle p-6 text-center">
      <div class="text-sm text-gray-900 mb-3">Not connected to Slack.</div>
      <button
        type="button"
        class="inline-flex items-center gap-2 px-4 py-2 rounded-md text-white text-sm font-medium"
        style="background-color: #4A154B"
        (click)="connectNow()"
        [disabled]="busy()">
        <i class="fa-brands fa-slack"></i>
        Connect Slack
      </button>
    </div>
  } @else {
    <div class="relative mb-3">
      <button
        type="button"
        class="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded border border-border-subtle hover:bg-row-hover text-left"
        (click)="toggleAccountMenu()">
        <span class="flex items-center gap-2 min-w-0">
          @if (account()?.picture) {
            <img [src]="$safeNavigationMigration(account()?.picture)" class="w-5 h-5 rounded-full flex-shrink-0" alt="" />
          } @else {
            <span class="w-5 h-5 rounded-full text-white text-[10px] flex items-center justify-center flex-shrink-0" style="background-color: #4A154B">
              <i class="fa-brands fa-slack"></i>
            </span>
          }
          <span class="min-w-0 truncate">
            <span class="text-xs text-gray-500">{{ account()?.user_name }}</span>
          </span>
        </span>
        <i class="fa-solid fa-chevron-down text-[10px] text-tertiary flex-shrink-0" [class.rotate-180]="accountMenuOpen()"></i>
      </button>

      @if (accountMenuOpen()) {
        <div class="absolute z-10 left-0 right-0 mt-1 rounded-md bg-card border border-border-subtle shadow-lg overflow-hidden">
          @if (account()?.email) {
            <div class="px-3 py-2 border-b border-border-subtle">
              <div class="text-xs text-gray-500 truncate">{{ account()?.email }}</div>
            </div>
          }
          <button
            type="button"
            class="w-full px-3 py-2 text-left text-sm text-status-error hover:bg-status-error-bg flex items-center gap-2 border-t border-border-subtle"
            (click)="disconnect(); closeAccountMenu()"
            [disabled]="busy()">
            <i class="fa-solid fa-link-slash"></i>
            Disconnect Slack
          </button>
        </div>
      }
    </div>

    @if (hasStatus()) {
      <div class="rounded-md border border-border-subtle bg-row-hover px-3 py-2 mb-3">
        <div class="flex items-start justify-between gap-2">
          <div class="flex items-center gap-2 min-w-0">
            @if (currentEmoji()) {
              <span class="text-lg leading-none">{{ emojiChar(currentEmoji()) }}</span>
            }
            <div class="min-w-0">
              <div class="text-sm font-medium text-gray-900 truncate">{{ currentText() || '(no description)' }}</div>
              @if (currentEndAt()) {
                <div class="text-[11px] text-gray-500">until {{ currentEndAt() | date:'MM/dd/yy hh:mm a' }}</div>
              }
            </div>
          </div>
          <div class="flex items-center gap-2 flex-shrink-0">
            <button type="button" class="text-xs text-gray-500 hover:underline" (click)="openForm()" [disabled]="busy()">Edit</button>
            <button type="button" class="text-xs text-status-error hover:underline" (click)="clearStatus()" [disabled]="busy()">Clear</button>
          </div>
        </div>
      </div>
    }

    @if (hasScheduled()) {
      <div class="rounded-md border border-status-warning bg-status-warning-bg px-3 py-2 mb-3">
        <div class="flex items-start justify-between gap-2 text-xs text-status-warning">
          <div class="min-w-0">
            <div class="flex items-center gap-1.5">
              @if (scheduledEmoji()) { <span class="text-base leading-none">{{ emojiChar(scheduledEmoji()) }}</span> }
              <strong class="truncate">{{ scheduledText() || '(no description)' }}</strong>
            </div>
            <div class="text-[11px] mt-0.5">
              <b>Turn On:</b> {{ scheduledStartAt() | date:'MM/dd/yy hh:mm a' }}
              @if (scheduledEndAt()) { · <b>Turn Off:</b> {{ scheduledEndAt() | date:'MM/dd/yy hh:mm a' }} }
            </div>
          </div>
          <button type="button" class="text-xs text-status-warning hover:underline flex-shrink-0" (click)="cancelScheduled()" [disabled]="busy()">Clear</button>
        </div>
      </div>
    }

    @if (!formOpen() && !scheduleOpen()) {
      <button
        type="button"
        class="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-interactive-primary text-white text-sm font-medium hover:bg-interactive-primary mb-2"
        (click)="openForm()">
        <i class="far fa-comment"></i>
        {{ hasStatus() ? 'Edit status' : 'Set status' }}
      </button>
      <button
        type="button"
        class="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-border-subtle text-sm text-gray-900 hover:bg-row-hover"
        (click)="openSchedule()">
        <i class="far fa-clock"></i>
        {{ hasScheduled() ? 'Edit schedule' : 'Schedule for later' }}
      </button>
    }

    @if (formOpen()) {
      <div class="rounded-md border border-border-subtle p-1.5">
        <label class="block text-xs text-gray-500 mb-1">What's your status?</label>
        <input
          type="text"
          class="w-full rounded border border-border-input px-2 py-1.5 text-sm mb-3"
          placeholder="E.g. In a meeting, On vacation, Out sick…"
          [value]="form.text"
          (input)="onFormText($any($event.target).value)" />

        <label class="block text-xs text-gray-500 mb-1">Emoji</label>
        <div class="grid grid-cols-6 gap-1.5 mb-3">
          @for (e of palette(); track e.code) {
            <button
              type="button"
              class="aspect-square flex items-center justify-center rounded border text-lg hover:bg-row-hover"
              [class.bg-status-info-bg]="form.emoji === e.code"
              [class.border-status-info]="form.emoji === e.code"
              [class.border-border-subtle]="form.emoji !== e.code"
              (click)="selectEmoji('form', e.code)"
              [title]="e.code">
              {{ e.char }}
            </button>
          }
        </div>

        <label class="block text-xs text-gray-500 mb-1">Turn off at (optional)</label>
        <app-date
          idInfo="slack-form-end"
          [isContainerStyle]="true"
          [label]="'Date'"
          [options]="{ placeholder: 'Select end date' }"
          [parent]="form"
          [time]="true"
          startKey="endAt"
          (onSave)="onFormEndAt($event)">
        </app-date>

        <div class="flex items-center gap-2 mt-3">
          <button
            type="button"
            class="flex-1 px-3 py-1.5 rounded-md bg-interactive-primary text-white text-xs font-medium hover:bg-interactive-primary disabled:opacity-50"
            (click)="apply()"
            [disabled]="busy()">
            Apply
          </button>
          <button type="button" class="px-3 py-1.5 rounded-md border border-border-input text-xs hover:bg-row-hover" (click)="closeForm()">Cancel</button>
        </div>
      </div>
    }

    @if (scheduleOpen()) {
      <div class="rounded-md border border-border-subtle p-1.5">
        <label class="block text-xs text-gray-500 mb-1">What's your status?</label>
        <input
          type="text"
          class="w-full rounded border border-border-input px-2 py-1.5 text-sm mb-3"
          placeholder="E.g. On vacation, Out sick…"
          [value]="scheduleForm.text"
          (input)="onScheduleText($any($event.target).value)" />

        <label class="block text-xs text-gray-500 mb-1">Emoji</label>
        <div class="grid grid-cols-6 gap-1.5 mb-3">
          @for (e of palette(); track e.code) {
            <button
              type="button"
              class="aspect-square flex items-center justify-center rounded border text-lg hover:bg-row-hover"
              [class.bg-status-info-bg]="scheduleForm.emoji === e.code"
              [class.border-status-info]="scheduleForm.emoji === e.code"
              [class.border-border-subtle]="scheduleForm.emoji !== e.code"
              (click)="selectEmoji('schedule', e.code)"
              [title]="e.code">
              {{ e.char }}
            </button>
          }
        </div>

        <label class="block text-xs text-gray-500 mb-1">Turn on at</label>
        <div class="mb-3">
          <app-date
            idInfo="slack-schedule-start"
            [isContainerStyle]="true"
            [label]="'Date'"
            [options]="{ placeholder: 'Select start date' }"
            [parent]="scheduleForm"
            [time]="true"
            startKey="startAt"
            (onSave)="onScheduleStartAt($event)">
          </app-date>
        </div>

        <label class="block text-xs text-gray-500 mb-1">Turn off at (optional)</label>
        <app-date
          idInfo="slack-schedule-end"
          [isContainerStyle]="true"
          [label]="'Date'"
          [options]="{ placeholder: 'Select end date' }"
          [parent]="scheduleForm"
          [time]="true"
          startKey="endAt"
          (onSave)="onScheduleEndAt($event)">
        </app-date>

        <div class="flex items-center gap-2 mt-3">
          <button
            type="button"
            class="flex-1 px-3 py-1.5 rounded-md bg-interactive-primary text-white text-xs font-medium hover:bg-interactive-primary disabled:opacity-50"
            (click)="saveSchedule()"
            [disabled]="busy()">
            Save schedule
          </button>
          <button type="button" class="px-3 py-1.5 rounded-md border border-border-input text-xs hover:bg-row-hover" (click)="closeSchedule()">Cancel</button>
        </div>
      </div>
    }

    @if (errorMsg(); as e) {
      <div class="mt-3 rounded-md bg-status-error-bg border border-status-error px-3 py-2 text-xs text-status-error">{{ e }}</div>
    }
  }
</div>
